import React, { useMemo, useState } from 'react';
import { Modal, Input, Select, Table, Space, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useGetMatGapWhInventory, useGetMatWarehouses } from '../../hooks/genericHooks';
import type { VwMatGapWhInventory } from '../../types';

export type MatGapMaterialSelectRow = {
  mat_id: string;
  description: string;
  unit: string;
  prod_group: string;
  ext_group: string;
  hierarchy_long: string;
  warehouse_id: number;
  onhand_qty: number;
};

export interface MatGapMaterialSelectProps {
  open: boolean;
  onOk: (rows: MatGapMaterialSelectRow[]) => void;
  onCancel: () => void;
  title?: string;
}

export const MatGapMaterialSelect: React.FC<MatGapMaterialSelectProps> = ({ open, onOk, onCancel, title }) => {
  const { data = [], isLoading } = useGetMatGapWhInventory() as any;
  const inventory: VwMatGapWhInventory[] = Array.isArray(data) ? data : (data?.data ?? []);

  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState<string>('');
  const [extGroupFilter, setExtGroupFilter] = useState<string>('');
  const [warehouseFilter, setWarehouseFilter] = useState<number | undefined>(undefined);
  // Multi-select: keep set of selected keys
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);

  const norm = (x: any) => (x == null ? '' : String(x)).trim().toLowerCase();

  const filtered = useMemo(() => {
    const searchVal = (search || '').trim().toLowerCase();
    const phraseMatches = Array.from(searchVal.matchAll(/"([^"]+)"/g)).map(m => m[1].trim()).filter(Boolean);
    const remaining = searchVal.replace(/"[^"]+"/g, ' ').trim();
    const tokens = remaining.split(/\s+/).filter(Boolean);
    const matchesSearch = (r: VwMatGapWhInventory) => {
      if (tokens.length === 0 && phraseMatches.length === 0) return true;
      const hay = `${r.mat_id} ${r.description || ''} ${r.unit || ''} ${r.prod_group || ''} ${r.hierarchy_long || ''} ${r.ext_group || ''}`.toLowerCase();
      if (phraseMatches.length > 0) return phraseMatches.every(p => hay.includes(p));
      return tokens.every(t => hay.includes(t));
    };
    return (inventory || [])
      .filter(r => (!groupFilter || norm(r.prod_group) === norm(groupFilter)))
      .filter(r => (!extGroupFilter || norm(r.ext_group) === norm(extGroupFilter)))
      .filter(r => (warehouseFilter == null ? true : r.warehouse_id === warehouseFilter))
      .filter(matchesSearch);
  }, [inventory, search, groupFilter, extGroupFilter, warehouseFilter]);

  const groupOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    (filtered || []).forEach(r => { const raw = r.prod_group?.trim(); if (raw) { const k = raw.toLowerCase(); if (!byKey.has(k)) byKey.set(k, raw); } });
    return Array.from(byKey.values()).sort().map(v => ({ value: v, label: v }));
  }, [filtered]);

  // Warehouses dropdown options (id -> name if available)
  const { data: whData = [] } = useGetMatWarehouses() as any;
  const warehouseOptions = useMemo(() => {
    const list = Array.isArray(whData) ? whData : (whData?.data ?? []);
    const opts: Array<{ value: number; label: string }> = (list || []).map((w: any) => ({ value: Number(w.id), label: String(w.name ?? w.id) }));
    // Also include any warehouse ids present in filtered data but missing from list
    const present = new Set<number>(opts.map((o: { value: number }) => o.value));
    (inventory || []).forEach(r => { if (!present.has(r.warehouse_id)) { present.add(r.warehouse_id); opts.push({ value: r.warehouse_id, label: String(r.warehouse_id) }); } });
    return opts.sort((a: { label: string }, b: { label: string }) => String(a.label).localeCompare(String(b.label)));
  }, [whData, inventory]);
  const extGroupOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    (filtered || []).forEach(r => { const raw = r.ext_group?.trim(); if (raw) { const k = raw.toLowerCase(); if (!byKey.has(k)) byKey.set(k, raw); } });
    return Array.from(byKey.values()).sort().map(v => ({ value: v, label: v }));
  }, [filtered]);

  const dataSource = useMemo(() => (filtered || []).map(r => ({ ...r, key: `${r.mat_id}-${r.warehouse_id}` })), [filtered]);
  // Map over FULL inventory for stable selection across filtering/pagination
  const fullMap = useMemo(() => {
    const m = new Map<string, VwMatGapWhInventory>();
    (inventory || []).forEach(r => m.set(`${r.mat_id}-${r.warehouse_id}`, r));
    return m;
  }, [inventory]);

  const columns: ColumnsType<VwMatGapWhInventory & { key: string }> = [
    { title: 'Material ID', dataIndex: 'mat_id', key: 'mat_id', width: 120 },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 80 },
    { title: 'Ext Group', dataIndex: 'ext_group', key: 'ext_group', width: 160 },
    { title: 'Warehouse', dataIndex: 'warehouse_id', key: 'warehouse_id', width: 120 },
    { title: 'On-hand', dataIndex: 'onhand_qty', key: 'onhand_qty', width: 110, align: 'right' },
  ];

  const handleOk = () => {
    const rows = (selectedRowKeys || [])
      .map(k => fullMap.get(String(k)))
      .filter((r): r is VwMatGapWhInventory => !!r)
      .map((r) => ({
        mat_id: r.mat_id,
        description: r.description,
        unit: r.unit,
        prod_group: r.prod_group,
        ext_group: r.ext_group,
        hierarchy_long: r.hierarchy_long,
        warehouse_id: r.warehouse_id,
        onhand_qty: r.onhand_qty,
      }));
    onOk(rows);
  };

  return (
    <Modal
      open={open}
      title={title || 'Select Materials'}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Add Selected"
      width={1000}
    >
      <Space style={{ marginBottom: 8 }} wrap>
        <Input
          placeholder='Search material/description... (supports multi-word AND and "phrases")'
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 220 }}
        />
        <Select
          placeholder="Group"
          allowClear
          value={groupFilter || undefined}
          onChange={(v) => setGroupFilter(v || '')}
          style={{ width: 160 }}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) => (String(option?.label || '')).toLowerCase().includes(input.toLowerCase())}
          options={groupOptions}
        />
        <Select
          placeholder="Ext Group"
          allowClear
          value={extGroupFilter || undefined}
          onChange={(v) => setExtGroupFilter(v || '')}
          style={{ width: 160 }}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) => (String(option?.label || '')).toLowerCase().includes(input.toLowerCase())}
          options={extGroupOptions}
        />
        <Select
          placeholder="Warehouse"
          allowClear
          value={warehouseFilter}
          onChange={(v) => setWarehouseFilter(typeof v === 'number' ? v : undefined)}
          style={{ width: 160 }}
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) => (String(option?.label || '')).toLowerCase().includes(input.toLowerCase())}
          options={warehouseOptions}
        />
      </Space>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey={(r) => r.key}
        loading={isLoading}
        size="small"
        pagination={{ current: page, pageSize, showSizeChanger: true, showTotal: () => `Filtered: ${dataSource.length} / Total: ${(inventory || []).length}`, onChange: (p, ps) => { setPage(p); setPageSize(ps); } }}
        rowSelection={{ selectedRowKeys, onChange: (keys) => setSelectedRowKeys(keys as React.Key[]), preserveSelectedRowKeys: true }}
        scroll={{ x: 'max-content' }}
      />
    </Modal>
  );
};

export default MatGapMaterialSelect;
